<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog financial-conciliation-review-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="financial-conciliation-review-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="financial-conciliation-review-modal-title">{{ text.modal.title }}</h3>
          <p>{{ text.modal.subtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.modal.closeAriaLabel"
          :disabled="loading"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content financial-conciliation-review-modal__content">
        <form class="app-modal__form" @submit.prevent="submitForm">
          <section class="app-modal__section">
            <div class="financial-conciliation-review-modal__detail-box">
              <small class="app-muted">{{ text.modal.fields.detail }}</small>
              <div class="app-strong">{{ detailTitle }}</div>
              <div class="financial-conciliation-review-modal__detail-row">
                <small class="app-muted">{{ detailDescription }}</small>
                <small class="app-muted">{{ detailAmountLabel }}</small>
              </div>
            </div>

            <div class="app-modal__grid three-columns">
              <label class="app-modal-field">
                <span>{{ text.modal.fields.date }}</span>
                <VueDatePicker
                  v-model="form.dateModel"
                  class="app-modal-datepicker"
                  menu-class-name="app-modal-datepicker-menu"
                  :enable-time-picker="false"
                  auto-apply
                  locale="es-MX"
                  format="dd/MM/yyyy"
                  hide-input-icon
                />
              </label>

              <label class="app-modal-field">
                <span>{{ text.modal.fields.amount }}</span>
                <AppMoneyInput
                  v-model="form.amount"
                  output-mode="number"
                  :decimal-places="2"
                  :minimum-value="0"
                  :currency="displayCurrency"
                  :disabled="true"
                />
              </label>

              <div class="app-modal-field">
                <span>{{ text.modal.fields.account }}</span>
                <multiselect
                  ref="accountSelect"
                  v-model="form.accountOption"
                  :options="accountOptions"
                  track-by="value"
                  label="label"
                  :multiple="false"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :preserve-search="false"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="false"
                  :max-height="220"
                  :placeholder="text.modal.placeholders.account"
                  class="app-modal-select"
                  @select="deactivateSelector"
                >
                  <template #noResult>
                    <span>{{ text.toolbar.emptyOption }}</span>
                  </template>
                  <template #noOptions>
                    <span>{{ text.toolbar.emptyOption }}</span>
                  </template>
                </multiselect>
              </div>
            </div>
          </section>

          <footer class="app-modal__footer">
            <div class="app-modal__footer-left">
              <button
                v-if="canDelete"
                type="button"
                class="app-table-icon-btn app-table-icon-btn--delete financial-conciliation-review-modal__delete-btn"
                :title="text.modal.actions.delete"
                :aria-label="text.modal.actions.delete"
                :disabled="loading"
                @click="emitRequestDelete"
              >
                <i class="fa fa-trash" aria-hidden="true"></i>
              </button>
            </div>
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" :disabled="loading" @click="emitClose">{{ text.modal.actions.cancel }}</button>
              <button type="submit" class="btn btn-primary" :disabled="loading">
                <i v-if="loading" class="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i>
                <span>{{ text.modal.actions.update }}</span>
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import { formatDateForApi, parsePresentationDate } from '@/utils/datePresentation';
import '@vuepic/vue-datepicker/dist/main.css';

const buildToday = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

export default {
  name: 'FinancialConciliationReviewModal',
  components: {
    VueDatePicker,
    AppMoneyInput,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    text: {
      type: Object,
      required: true,
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    row: {
      type: Object,
      default: null,
    },
  },
  emits: ['close', 'submit', 'request-delete'],
  data() {
    return {
      form: this.createInitialForm(),
    };
  },
  computed: {
    accountOptions() {
      const rows = Array.isArray(this.accounts) ? this.accounts : [];
      return rows
        .map((account) => {
          const id = Number(account?.id || 0);
          if (id <= 0) {
            return null;
          }

          return {
            value: id,
            label: String(account?.name || '').trim(),
          };
        })
        .filter(Boolean);
    },
    canDelete() {
      return Number(this.row?.can_delete || 0) === 1 && Number(this.row?.id || 0) > 0;
    },
    detailTitle() {
      return String(this.row?.concept_title || this.row?.concept || '-').trim() || '-';
    },
    detailDescription() {
      const label = String(this.row?.concept_detail_label || 'Concepto:').trim();
      const detail = String(this.row?.concept_detail || this.row?.concept || '').trim();
      return `${label} ${detail}`.trim();
    },
    detailAmountLabel() {
      return String(this.row?.concept_detail_amount_label || this.row?.amount_label || '').trim();
    },
    displayCurrency() {
      return String(this.row?.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
  },
  watch: {
    show(nextValue) {
      if (!nextValue) {
        return;
      }

      this.hydrateForm();
    },
    row: {
      deep: true,
      handler() {
        if (this.show) {
          this.hydrateForm();
        }
      },
    },
  },
  methods: {
    createInitialForm() {
      return {
        dateModel: parsePresentationDate(buildToday()),
        accountOption: null,
        amount: 0,
      };
    },
    resolveOption(accountId) {
      const normalized = Number(accountId || 0);
      if (normalized <= 0) {
        return null;
      }

      return this.accountOptions.find((option) => Number(option?.value || 0) === normalized)
        || null;
    },
    resolveDateModel(value) {
      const parsed = parsePresentationDate(value);
      if (parsed) {
        return parsed;
      }

      return parsePresentationDate(buildToday());
    },
    resolvePayloadDate(value) {
      const normalized = parsePresentationDate(value);
      if (!normalized) {
        return buildToday();
      }

      return formatDateForApi(normalized, { allowString: true }) || buildToday();
    },
    hydrateForm() {
      const row = this.row && typeof this.row === 'object' ? this.row : null;
      if (!row) {
        this.form = this.createInitialForm();
        return;
      }

      this.form = {
        dateModel: this.resolveDateModel(row?.raw_date || row?.date || buildToday()),
        accountOption: this.resolveOption(row?.financial_account_id),
        amount: Number(row?.amount || 0) || 0,
      };
    },
    deactivateSelector() {
      this.$nextTick(() => {
        this.$refs?.accountSelect?.deactivate?.();
      });
    },
    emitClose() {
      if (this.loading) {
        return;
      }

      this.$emit('close');
    },
    emitRequestDelete() {
      if (this.loading || !this.canDelete) {
        return;
      }

      this.$emit('request-delete', this.row);
    },
    submitForm() {
      const accountId = Number(this.form.accountOption?.value || 0);
      const row = this.row || {};
      const parsedTransactionId = Number(row?.id);
      const transactionId = Number.isFinite(parsedTransactionId) && parsedTransactionId > 0
        ? parsedTransactionId
        : 0;

      this.$emit('submit', {
        transaction_id: transactionId,
        reference_id: Number(row?.reference_id || 0),
        reference_type: String(row?.reference_type || '').trim(),
        type_reference: String(row?.type_reference || '').trim(),
        financial_account_id: accountId,
        date: this.resolvePayloadDate(this.form.dateModel),
      });
    },
  },
};
</script>

<style scoped>
.financial-conciliation-review-modal {
  width: min(680px, calc(100vw - 24px));
  height: min(460px, calc(100vh - 24px));
  display: flex;
  flex-direction: column;
}

.financial-conciliation-review-modal__content {
  flex: 1;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
}

.financial-conciliation-review-modal .app-modal__form {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.financial-conciliation-review-modal .app-modal__section {
  flex: 1;
}

.financial-conciliation-review-modal__detail-box {
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 14px;
}

.financial-conciliation-review-modal__detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
}

.financial-conciliation-review-modal__detail-row .app-muted:first-child {
  flex: 1;
  min-width: 0;
}

.financial-conciliation-review-modal__detail-row .app-muted:last-child {
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
  min-width: 120px;
}

@media (max-width: 860px) {
  .financial-conciliation-review-modal__detail-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .financial-conciliation-review-modal__detail-row .app-muted:last-child {
    min-width: 0;
    margin-left: 0;
  }
}

.app-modal__footer-left {
  margin-right: auto;
}

.financial-conciliation-review-modal__delete-btn {
  min-height: 38px;
  height: 38px;
  width: 44px;
  min-width: 44px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
